import React, { HTMLAttributes, PropsWithChildren, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { scheduleIdle } from 'src/components/Scripts/scheduleIdle';
import { SectionSkeleton } from 'src/elements/Skeletons/Skeletons';

type ModuleLike = { default?: React.ComponentType<any> } | React.ComponentType<any>;

export type LazyHydrateProps = PropsWithChildren<
    {
        /** Must be identical server and client — useId() is not, the entry trees differ. */
        sectionId: string;
        /**
         * Must resolve synchronously on the server (component / module) so SSR
         * HTML stays crawlable. On the client it may return a Promise from
         * `import()` so the chunk fetch can wait for idle / near-viewport.
         */
        load: () => ModuleLike | Promise<ModuleLike>;
        delay?: number;
        minHeight?: string;
    } & HTMLAttributes<any>
>;

function unwrap(mod: ModuleLike): React.ComponentType<any> {
    if (typeof mod === 'function') {
        return mod as React.ComponentType<any>;
    }
    return ((mod as { default?: React.ComponentType<any> }).default ?? mod) as React.ComponentType<any>;
}

/**
 * SEO-safe deferred section loader.
 *
 * - SSR: sync-render via `load()` so footer/home markup stays in page source.
 * - Client: keep that SSR HTML (via captured innerHTML) until idle/IO, then
 *   load the chunk with `React.lazy` + Suspense and hydrate the section.
 */
export function LazyHydrate(props: LazyHydrateProps) {
    const { sectionId: id, load, delay = 2000, minHeight, children, ...rest } = props;
    const isServer = typeof window === 'undefined';

    if (isServer) {
        const result = load();
        if (result && typeof (result as Promise<ModuleLike>).then === 'function') {
            return (
                <section id={id} style={minHeight ? { minHeight } : undefined} {...rest}>
                    {children}
                </section>
            );
        }
        const Comp = unwrap(result as ModuleLike);
        return (
            <section id={id} style={minHeight ? { minHeight } : undefined} {...rest}>
                <Comp />
            </section>
        );
    }

    return <ClientLazyHydrate id={id} load={load} delay={delay} minHeight={minHeight} {...rest} />;
}

function ClientLazyHydrate({
    id,
    load,
    delay = 2000,
    minHeight,
    ...rest
}: {
    id: string;
    load: () => ModuleLike | Promise<ModuleLike>;
    delay?: number;
    minHeight?: string;
} & HTMLAttributes<any>) {
    const ref = useRef<HTMLElement | null>(null);
    const [ready, setReady] = useState(false);

    // Capture SSR markup before we swap to the lazy React tree so hydration
    // does not blank below-fold sections (previous empty-__html bug).
    const [ssrHtml] = useState(() => document.getElementById(id)?.innerHTML ?? '');

    const LazyChild = useMemo(
        () =>
            React.lazy(async () => {
                const mod = await Promise.resolve(load());
                return { default: unwrap(mod) };
            }),
        [load],
    );

    useEffect(() => {
        const node = ref.current;
        let cancelIdle: (() => void) | undefined;
        let cancelled = false;

        const enable = () => {
            if (!cancelled) setReady(true);
        };

        // Client-side navigations have no SSR HTML — load promptly.
        if (!ssrHtml) {
            cancelIdle = scheduleIdle(enable, 0);
            return () => {
                cancelled = true;
                cancelIdle?.();
            };
        }

        if (!node || typeof IntersectionObserver === 'undefined') {
            cancelIdle = scheduleIdle(enable, delay);
            return () => {
                cancelled = true;
                cancelIdle?.();
            };
        }

        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    io.disconnect();
                    cancelIdle = scheduleIdle(enable, Math.min(delay, 500));
                }
            },
            { rootMargin: '300px' },
        );
        io.observe(node);
        const fallback = window.setTimeout(enable, Math.max(delay, 4000));

        return () => {
            cancelled = true;
            io.disconnect();
            cancelIdle?.();
            window.clearTimeout(fallback);
        };
    }, [delay, ssrHtml]);

    const spaFallback = <SectionSkeleton height={minHeight || '200px'} />;
    const suspenseFallback = ssrHtml ? <span dangerouslySetInnerHTML={{ __html: ssrHtml }} /> : spaFallback;

    if (ready) {
        return (
            <section
                id={id}
                ref={ref as React.RefObject<HTMLElement>}
                style={minHeight ? { minHeight } : undefined}
                {...rest}
            >
                <Suspense fallback={suspenseFallback}>
                    <LazyChild />
                </Suspense>
            </section>
        );
    }

    // Client navigations have no SSR markup — reserve space with a skeleton.
    if (!ssrHtml) {
        return (
            <section
                id={id}
                ref={ref as React.RefObject<HTMLElement>}
                style={minHeight ? { minHeight } : undefined}
                {...rest}
            >
                {spaFallback}
            </section>
        );
    }

    return (
        <section
            id={id}
            ref={ref as React.RefObject<HTMLElement>}
            style={minHeight ? { minHeight } : undefined}
            suppressHydrationWarning
            dangerouslySetInnerHTML={{ __html: ssrHtml }}
            {...rest}
        />
    );
}
